import React from 'react';
import { Link } from 'react-router-dom';
import { PartyState, Translatable, PartyUpdate } from '../../_generatedTypes.g';
import { getLocaleCode, getTranslator, LanguageCode } from '../../Translator';
import { useDispatch } from 'react-redux';
import { InlineFormItem, SaveChangesResult } from '../ui/InlineFormItem';
import { getPartyById, canEditPartyStructure } from '../../selectors';
import { api, ReadAs } from '../../api';
import { deleteParty, partyNameChanged } from '../../features/party/partiesSlice';
import { useAppSelector } from '../../hooks';

interface PartyItemProps {
    partyId: number
}

export const PartyItem = (props: PartyItemProps) => {
    const account = useAppSelector(state => state.clientState.Account);
    const parties = useAppSelector(state => state.parties);
    const elections = useAppSelector(state => state.elections);
    const language = useAppSelector(state => state.language);

    const t = getTranslator(language);

    const party = getPartyById(parties, props.partyId);

    return canEditPartyStructure(account, parties, elections, props.partyId)
        ? <EditablePartyItem party={party} language={t.languageCode} isAdmin={account.IsAdmin} />
        : <PartyLink party={party} lang={t.languageCode} />
};

type EditablePartyItemProps = {
    party: PartyState;
    language: LanguageCode;
    isAdmin: boolean;
};

const EditablePartyItem = (props: EditablePartyItemProps) => {
    const justDutch = useAppSelector(state => state.clientState.JustDutch);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);

    const dispatch = useDispatch();
    const t = getTranslator(props.language);

    const [newName, setNewName] = React.useState<Translatable>(props.party.Name);
    const [errors, setErrors] = React.useState<string[]>([]);

    const formRef: React.RefObject<HTMLFormElement|null> = React.createRef();

    const handleChangeFor = (language: LanguageCode) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewName({ ...newName, [language]: e.currentTarget.value });
        if (formRef.current && formRef.current.checkValidity()) {
            setErrors([]);
        } else {
            setErrors([t['Only use letters or numbers for this field']]);
        }
    }

    const saveChanges = async (): Promise<SaveChangesResult> => {
        const body: PartyUpdate = {
            RowVersion: props.party.RowVersion,
            Updates: { 'Name_EN': newName.EN, 'Name_NL': newName.NL },
        };
        return await api(token).Parties.Patch<SaveChangesResult>(props.party.PartyId, body)
            .ok((_, response) => {
                const rowVersion = response.headers.get('ETag')!;
                dispatch(partyNameChanged({ partyId: props.party.PartyId, newName, rowVersion }));
                return { success: true };
            }).anyStatus(errors => ({ errors, success: false }), ReadAs.ValidationErrors);
    }

    return <InlineFormItem formRef={formRef}
        errors={errors}
        className="election__party"
        saveChanges={saveChanges}
        key={props.party.PartyId}
        position={props.party.Position}
        link={<PartyLink party={props.party} lang={t.languageCode} />}
        options={<ul><li><a onClick={() => deleteParty(props.party.PartyId, token, dispatch)}>{t['Delete']}</a></li></ul>}
        optionsTitle={t['Options']}
        draggable={false}
        enablePopup={props.isAdmin || props.party.IsCurrentAccountPartyAdmin}
        enableEditButton={props.isAdmin || props.party.IsCurrentAccountPartyAdmin}>
        <label>
            NL:
            <input autoFocus={t.languageCode === 'NL'}
                lang={getLocaleCode('NL')}
                value={newName.NL}
                onChange={handleChangeFor('NL')}
                maxLength={50} />
        </label>
        {!justDutch && <label>
            EN:
            <input autoFocus={t.languageCode === 'EN'}
                lang={getLocaleCode('EN')}
                value={newName.EN}
                onChange={handleChangeFor('EN')}
                maxLength={50} />
        </label>}
    </InlineFormItem>
}

const PartyLink = ({ party, lang }: { party: PartyState, lang: LanguageCode }) =>
    <Link
        to={`/party/${party.PartyId}`}
        id={`Election-Party-${party.PartyId}`}
        className="election__party linkbutton button--important"
        key={party.PartyId}
        draggable="false">
        {party.Name[lang]}
    </Link>
